import { isCancelledError } from "@tanstack/react-query";
import { ActAsSessionController } from "./ActAsSessionController";
import { ActAsSessionEndedError, currentActAsSession, endActAsSession } from "./actAsSession";
import { useEffect, lazy, Suspense, type ReactNode, useRef } from "react";
import { JM_ENVIRONMENT, app } from "app/Firebase";
import { getApiUrl } from "lib/apiUrl";
import "app/common.css";
import Toaster from "src/app/Toaster";
import "src/app/app.css"; // This is currently imported both here and in App.tsx... not needed in App?
import mobileStyles from "src/app/mobile.css?inline";
import { useCurrentTenant } from "app/jm";
import { mobileAndTabletCheck } from "app/jm";
import { QueryClientProvider, useQuery, useQueryClient } from "@tanstack/react-query";
import { Login } from "src/app/Login";
import { assign, createMachine, fromPromise } from "xstate";
import { useMachine } from "@xstate/react";
import axios from "axios";
import { currentUserQueryOptions } from "./useCurrentUserData";
import type { CardProps } from "@blueprintjs/core";
import { Alert, Button, Card, Spinner, NonIdealState } from "@blueprintjs/core";
import { assert } from "lib/util/assert";
import { getAuth, type User } from "firebase/auth";
import { ReactQueryDevtoolsPanel } from "@tanstack/react-query-devtools";
import devToolsConfigAtom from "./devTools/devToolsConfigAtom";
import { useAtom, useAtomValue } from "jotai";
import * as Sentry from "@sentry/react";
import NetworkStatus from "./NetworkStatus";
import { getStorage, ref, getDownloadURL, uploadBytes } from "firebase/storage";
import overlayElementsAtom from "./uiState/overlayElementsAtom";
import { enableMapSet } from "immer";
import { setFaviconWithBaseIcon } from "./generateTenantFavicon";
import RouteComponentExecutor from "./router/RouteComponentExecutor";
import { queryClient } from "./queryClient";
import { useFirebaseTenantDownloadURL } from "./useFirebaseTenantDownloadURL";
import { getMobileComponentStackBaseHref } from "./router/mobileComponentStackRoutes";
import { getTenantId } from "./tenant";
import { getComponentRouteSignature } from "./router/componentRouteResolver";
import { useLocation } from "@tanstack/react-router";
import { GlobalDirtyChecker } from "./GlobalDirtyChecker";
import { updateDialogModeAtom } from "./components/TaskBar/UpdateButton";
import { ReloadableErrorBoundary } from "lib/view/ReloadableErrorBoundary";
import { McpConsent } from "./McpConsent";
import { resetLocalStorageOnce } from "./localStorageReset";
import { UserStorageBoundary } from "./UserStorageBoundary";
import { captureAppError, getErrorStatusCode } from "lib/sentry/errorReporting";

const auth = getAuth(app);

const storage = getStorage(app);

const isMobileOrTablet = mobileAndTabletCheck();

function getCurrentComponentRouteSignature() {
  return getComponentRouteSignature({
    environment: JM_ENVIRONMENT,
    pathname: window.location.pathname,
    search: window.location.search,
  });
}

function clearRouteUrl({
  preservePendingComponentRoute = false,
}: {
  preservePendingComponentRoute?: boolean;
} = {}) {
  if (new URLSearchParams(window.location.search).has("mcp_request")) {
    return;
  }
  if (preservePendingComponentRoute && getCurrentComponentRouteSignature()) {
    return;
  }

  const tenantId = getTenantId();
  if (!tenantId) return;
  window.history.replaceState(
    {},
    document.title,
    getMobileComponentStackBaseHref(JM_ENVIRONMENT, tenantId),
  );
}

// Bundle these styles with the app so startup does not depend on a separate CSS request.
if (isMobileOrTablet) {
  const style = document.createElement("style");
  style.textContent = mobileStyles;
  document.head.appendChild(style);
}

const DesktopApp = lazy(() => import("src/app/DesktopApp"));
const AdminApp = lazy(() => import("src/app/AdminApp"));
const MobileApp = lazy(() => import("src/app/MobileApp"));
const CustomerApp = lazy(() => import("src/app/CustomerApp"));

// See: https://immerjs.github.io/immer/map-set
enableMapSet();

// Read url query param
const urlParams = new URLSearchParams(window.location.search);
const isCustomer = urlParams.get("customer");

type UserAuthenticityFailure = "missing" | "disabled" | "deleted";
type AuthenticatedFirebaseUser = Pick<User, "uid" | "tenantId" | "email">;

function getUserAuthenticityFailure(
  user:
    | {
        isActive?: boolean | null;
        deleted?: boolean | null;
      }
    | null
    | undefined,
): UserAuthenticityFailure | null {
  if (!user) return "missing";
  if (!user.isActive) return "disabled";
  if (user.deleted) return "deleted";
  return null;
}

function Status({ text, cardProps }: { text?: string; cardProps?: CardProps }) {
  return (
    <Card
      elevation={2}
      style={{
        maxWidth: 180,
        textAlign: "center",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        padding: 15,
        background: "white",
        borderRadius: 12,
      }}
      {...cardProps}
    >
      <Spinner size={isMobileOrTablet ? 32 : 16} />
      {text && (
        <span style={{ fontSize: 15, paddingLeft: 12, paddingTop: 0 }}>
          {text}
        </span>
      )}
    </Card>
  );
}

const TenantBackground = ({ children }: { children?: ReactNode }) => {
  const { data: wallpaperUrl } =
    useFirebaseTenantDownloadURL(`public/wallpaper.jpg`);

  const { themeColour } = useCurrentTenant();

  useEffect(() => {
    if (themeColour) {
      setFaviconWithBaseIcon("", {
        iconUrl: "/assets/img/logo_plain.png",
        textColor: "#fff",
        tintColor: themeColour,
      });
    }
  }, [themeColour]);

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
        backgroundImage: wallpaperUrl ? `url(${wallpaperUrl})` : "none",
        backgroundPosition: "center",
        backgroundSize: "cover",
      }}
    >
      {children}
    </div>
  );
};

function DeferredRouteLoginToast() {
  const location = useLocation();
  const notifiedRouteRef = useRef<string | null>(null);

  useEffect(() => {
    const signature = getComponentRouteSignature({
      environment: JM_ENVIRONMENT,
      pathname: location.pathname,
      search: location.searchStr,
    });

    if (!signature) {
      notifiedRouteRef.current = null;
      return;
    }

    if (notifiedRouteRef.current === signature) {
      return;
    }

    notifiedRouteRef.current = signature;
    Toaster.info("Please Login first to access this link.");
  }, [location.pathname, location.searchStr]);

  return null;
}

export const authMachine = createMachine({
  id: "authMachine",
  types: {} as {
    context: {
      // This causes OOM errors when using hot module reloading
      // https://github.com/statelyai/xstate/issues/4645
      // The issue is any object that contains circular references
      // causes xstate to recurse infinitely when persisting context
      // between reloads. :/
      // The answer is not to store the firebase user in context, but
      // just the details we need
      //firebaseUser: User | null;
      firebaseUserDetails: {
        uid: string;
        tenantId: string | null;
      } | null;
      userAuthenticityFailure: UserAuthenticityFailure | null;
    };
    events:
      | {
          type: "FIREBASE_SIGNED_IN";
          user: AuthenticatedFirebaseUser;
        }
      | { type: "FIREBASE_SIGNED_OUT" }
      | { type: "RETRY_AUTHENTICATION" }
      | { type: "RETRY_LOAD_TENANT" }
      | { type: "LOG_OUT" };
    actions:
      | {
          type: "assignFirebaseUser";
          params: { user: AuthenticatedFirebaseUser };
        }
      | {
          type: "setSentryUser";
          params: { user: AuthenticatedFirebaseUser };
        }
      | { type: "unassignFirebaseUser" }
      | {
          type: "assignUserAuthenticityFailure";
          params: { failure: UserAuthenticityFailure | null };
        }
      | { type: "setFirebaseTenantId"; params: { tenantId: string } }
      | { type: "reloadPage" }
      | { type: "signOutFirebase" }
      | { type: "showUserAuthenticityError" }
      | { type: "notifyTenantMismatch" };
  },
  initial: "loading",
  context: {
    //firebaseUser: null,
    firebaseUserDetails: null,
    userAuthenticityFailure: null,
  },
  states: {
    loading: {
      type: "parallel",
      states: {
        loadingTenant: {
          initial: "loading",
          states: {
            loading: {
              invoke: {
                id: "loadTenant",
                src: "loadTenant",
                onDone: {
                  target: "done",
                  actions: [
                    {
                      type: "setFirebaseTenantId",
                      params: ({ event }) => ({ tenantId: event.output }),
                    },
                  ],
                },
                onError: {
                  target: "failed",
                  actions: ({ event }) => {
                    if (getErrorStatusCode(event.error) !== 404) {
                      captureAppError({
                        error: event.error,
                        componentName: "AuthContext",
                        tags: { stage: "loadTenant" },
                      });
                    }
                  },
                },
              },
            },
            failed: {
              on: { RETRY_LOAD_TENANT: "loading" },
            },
            done: {
              type: "final",
            },
          },
        },
        loadingFirebase: {
          initial: "loading",
          // Keep observing Firebase after its first result while tenant loading can retry.
          on: {
            FIREBASE_SIGNED_IN: {
              target: ".done",
              actions: [
                {
                  type: "assignFirebaseUser",
                  params: ({ event }) => ({ user: event.user }),
                },
                {
                  type: "setSentryUser",
                  params: ({ event }) => ({ user: event.user }),
                },
              ],
            },
            FIREBASE_SIGNED_OUT: {
              target: ".done",
              actions: [{ type: "unassignFirebaseUser" }],
            },
          },
          states: {
            loading: {},
            done: {
              type: "final",
            },
          },
        },
      },
      onDone: [
        {
          target: "loggingIn",
          guard: "hasFirebaseUser",
        },
        {
          target: "loggedOut",
        },
      ],
    },
    loggedOut: {
      on: {
        FIREBASE_SIGNED_IN: {
          target: "loggingIn",
          actions: [
            {
              type: "assignFirebaseUser",
              params: ({ event }) => ({ user: event.user }),
            },
            {
              type: "setSentryUser",
              params: ({ event }) => ({ user: event.user }),
            },
          ],
        },
      },
    },
    loggingIn: {
      initial: "checkingTenantMatches",
      states: {
        checkingTenantMatches: {
          always: [
            {
              target: "checkingUserAuthenticity",
              guard: "checkTenantMatches",
            },
            {
              target: "#authMachine.loggedOut",
              actions: ["signOutFirebase", "notifyTenantMismatch"],
            },
          ],
        },
        checkingUserAuthenticity: {
          invoke: {
            id: "checkUserAuthenticity",
            src: "checkUserAuthenticity",
            onDone: {
              target: "evaluatingUserAuthenticity",
              actions: [
                {
                  type: "assignUserAuthenticityFailure",
                  params: ({ event }) => ({ failure: event.output }),
                },
              ],
            },
            onError: {
              target: "authenticationFailed",
            },
          },
        },
        evaluatingUserAuthenticity: {
          always: [
            {
              target: "#authMachine.loggedOut",
              guard: "hasUserAuthenticityFailure",
              actions: ["signOutFirebase", "showUserAuthenticityError"],
            },
            {
              target: "resettingLocalStorage",
            },
          ],
        },
        resettingLocalStorage: {
          invoke: {
            src: "resetLocalStorage",
            onDone: { target: "done" },
          },
        },
        authenticationFailed: {
          on: {
            RETRY_AUTHENTICATION: {
              target: "checkingUserAuthenticity",
            },
            LOG_OUT: {
              actions: ["signOutFirebase"],
            },
          },
        },
        done: {
          type: "final",
        },
      },
      on: {
        FIREBASE_SIGNED_OUT: {
          target: "#authMachine.loggedOut",
          actions: [{ type: "unassignFirebaseUser" }],
        },
      },
      onDone: {
        target: "loggedIn",
      },
    },
    loggedIn: {
      on: {
        FIREBASE_SIGNED_OUT: {
          target: "loggingOut",
        },
      },
    },
    // The firebase user is already logged out at this point
    // but we use this state to show a separate "logging out"
    // screen as otherwise open components will render oddly
    // due to the sudden lack of a logged in user.
    loggingOut: {
      // Reload the page to clear all global state
      entry: [{ type: "reloadPage" }],
    },
  },
});

function AuthContext() {
  const { tenantId } = useCurrentTenant();
  const queryClient = useQueryClient();

  const [state, send] = useMachine(
    authMachine.provide({
      actors: {
        resetLocalStorage: fromPromise(async () => {
          try {
            return resetLocalStorageOnce();
          } catch (error) {
            // Unavailable storage must not block login.
            Sentry.captureException(error);
            return false;
          }
        }),
        loadTenant: fromPromise(async ({ signal }) => {
          //console.log("SERVICE loadTenant");
          if (tenantId === "jmadmin") {
            return "jmadmin";
          }
          const response = await axios.get(
            getApiUrl(`/api/tenant/${tenantId}`),
            { timeout: 15_000, signal },
          );
          return response.data.firebaseTenantId;
        }),
        checkUserAuthenticity: fromPromise(async () => {
          //console.log("SERVICE checkUserExistsInJm");
          // This both checks the user exists, and also serves to preload
          // portal item data so we don't have to wait for menu to load.
          if (tenantId === "jmadmin") {
            return null;
          }
          const previousActAs = currentActAsSession();
          const readCurrentUser = () =>
            queryClient.fetchQuery({
              ...currentUserQueryOptions(),
              // Each authentication attempt must recheck the account status.
              staleTime: 0,
            });
          let response;
          try {
            response = await readCurrentUser();
          } catch (error) {
            if (
              !(error instanceof ActAsSessionEndedError) &&
              !(
                previousActAs &&
                !currentActAsSession() &&
                isCancelledError(error)
              )
            )
              throw error;
            // Authentication is a read: reload after expiry can safely use the base login.
            response = await readCurrentUser();
          }
          return getUserAuthenticityFailure(response.currentUser);
        }),
      },
      actions: {
        signOutFirebase: () => {
          //console.log("ACTION signOutFirebase");
          clearRouteUrl({ preservePendingComponentRoute: true });
          void endActAsSession().then(() => auth.signOut());
        },
        showUserAuthenticityError: ({ context }) => {
          if (context.userAuthenticityFailure === "deleted") {
            Toaster.error(
              "Your account has been deleted. Please contact support if you believe this is an error",
            );
          } else if (context.userAuthenticityFailure === "disabled") {
            Toaster.error(
              "Your account has been disabled. Please contact support if you believe this is an error",
            );
          } else {
            Toaster.error(
              "Your account does not exist in Job Manager. Please contact support",
            );
          }
        },
        notifyTenantMismatch: () => {
          Toaster.error("You were logged out because tenant changed.");
        },
        assignFirebaseUser: assign({
          firebaseUserDetails: (_, params) => {
            return { uid: params.user.uid, tenantId: params.user.tenantId };
          },
        }),
        unassignFirebaseUser: assign({
          firebaseUserDetails: () => {
            return null;
          },
        }),
        assignUserAuthenticityFailure: assign({
          userAuthenticityFailure: (_, params) => params.failure,
        }),
        setSentryUser: (_, params) => {
          if (params.user.email) {
            Sentry.setUser({
              email: params.user.email,
            });
          }
        },
        setFirebaseTenantId: (_, params) => {
          //console.log("ACTION setFirebaseTenantId");
          if (params.tenantId === "jmadmin") {
            return;
          }
          auth.tenantId = params.tenantId;
        },
        reloadPage: () => {
          clearRouteUrl();
          window.location.reload();
        },
      },
      guards: {
        // Because if we only change auth.tenantId, the current user doesn't change
        // See: https://cloud.google.com/identity-platform/docs/multi-tenancy-authentication#sign_in_with_tenants
        checkTenantMatches: ({ context }) => {
          //console.log("Checking tenants match guard", auth, context);
          assert(context.firebaseUserDetails);
          return context.firebaseUserDetails.tenantId === auth.tenantId;
        },
        hasFirebaseUser: ({ context }) => {
          //console.log("Has firebase user guard", auth, context);
          return !!context.firebaseUserDetails;
        },
        hasUserAuthenticityFailure: ({ context }) => {
          return context.userAuthenticityFailure !== null;
        },
      },
    }),
  );
  //console.log({ state: state.value, context: state.context });

  useEffect(() => {
    auth.onAuthStateChanged((user) => {
      //console.log("Firebase auth state changed", user);
      if (user) {
        send({
          type: "FIREBASE_SIGNED_IN",
          user: user,
        });
      } else {
        send({ type: "FIREBASE_SIGNED_OUT" });
      }
    });
  }, [send]);

  if (isCustomer) {
    return (
      <TenantBackground>
        <Suspense>
          <CustomerApp />
        </Suspense>
      </TenantBackground>
    );
  }

  if (!tenantId) {
    return (
      <div>
        <p>Job Manager: Please Specify a Tenant</p>
      </div>
    );
  }

  if (state.matches({ loading: { loadingTenant: "failed" } })) {
    return (
      <TenantBackground>
        <Card elevation={2} style={{ maxWidth: 420, padding: 20, borderRadius: 12 }}>
          <NonIdealState
            icon="offline"
            title="Can’t connect to Job Manager"
            description="Please try again. If the problem continues, contact support."
            action={
              <Button
                icon="refresh"
                intent="primary"
                text="Try again"
                onClick={() => send({ type: "RETRY_LOAD_TENANT" })}
              />
            }
          />
        </Card>
      </TenantBackground>
    );
  }

  // Initialising tenant and firebase
  if (state.matches("loading")) {
    return (
      <TenantBackground>
        <Status />
      </TenantBackground>
    );
  }

  if (state.matches({ loggingIn: "authenticationFailed" })) {
    return (
      <TenantBackground>
        <Card
          elevation={2}
          style={{
            maxWidth: 420,
            padding: 20,
            background: "white",
            borderRadius: 12,
          }}
        >
          <NonIdealState
            icon="error"
            title="Unable to finish signing in"
            description="Job Manager could not load your account because of an unexpected server error. Your login has been kept, so you can try again."
            action={
              <div style={{ display: "flex", gap: 10 }}>
                <Button
                  icon="log-out"
                  text="Log out"
                  onClick={() => send({ type: "LOG_OUT" })}
                />
                <Button
                  icon="refresh"
                  intent="primary"
                  text="Try again"
                  onClick={() => send({ type: "RETRY_AUTHENTICATION" })}
                />
              </div>
            }
          />
        </Card>
      </TenantBackground>
    );
  }

  // Actually logging in
  if (state.matches("loggingIn")) {
    return (
      <TenantBackground>
        <Status text="Authenticating..." />
      </TenantBackground>
    );
  }

  if (state.matches("loggingOut")) {
    return (
      <TenantBackground>
        <Status text="Logging out..." />
      </TenantBackground>
    );
  }

  if (state.matches("loggedOut")) {
    return (
      <TenantBackground>
        <DeferredRouteLoginToast />
        <Login />
      </TenantBackground>
    );
  }

  if (state.matches("loggedIn")) {
    const mcpRequest = new URLSearchParams(window.location.search).get(
      "mcp_request",
    );
    if (mcpRequest !== null) {
      return (
        <TenantBackground>
          <McpConsent request={mcpRequest} />
        </TenantBackground>
      );
    }
    if (tenantId === "jmadmin") {
      return (
        <Suspense fallback={<Status text="Loading..." />}>
          <AdminApp />
        </Suspense>
      );
    }
    return (
      <TenantBackground>
        <ActAsSessionController />
        <UserStorageBoundary tenantId={tenantId}>
          <RouteComponentExecutor enabled={true} />
          <Suspense fallback={<Status text="Loading..." />}>
            {isMobileOrTablet ? (
              <MobileApp />
            ) : (
              <DesktopApp key={`${tenantId}-${auth.currentUser?.uid}`} />
            )}
          </Suspense>
        </UserStorageBoundary>
      </TenantBackground>
    );
  }
}

export const App = () => {
  const [devToolsConfig, setDevToolsConfig] = useAtom(devToolsConfigAtom);
  const [overlays, setOverlays] = useAtom(overlayElementsAtom);
  const updateDialogMode = useAtomValue(updateDialogModeAtom);

  return (
    <QueryClientProvider client={queryClient}>
      <GlobalDirtyChecker disabled={updateDialogMode === "automatic"} />
      {devToolsConfig.reactQueryDevTools.enabled && (
        <ReactQueryDevtoolsPanel
          style={{
            position: "fixed",
            bottom: 0,
            zIndex: 999999,
            height: 300,
            width: "100%",
          }}
          onClose={() => {
            setDevToolsConfig((cfg) => ({
              ...cfg,
              reactQueryDevTools: {
                ...cfg.reactQueryDevTools,
                enabled: false,
              },
            }));
          }}
        />
      )}
      <AuthContext />
      <NetworkStatus />
      {overlays.map(({ id, content }) => (
        <ReloadableErrorBoundary
          key={id}
          fallbackProps={{ action: undefined }}
          wrapFallback={(fallback) => (
            <Alert
              isOpen={true}
              onClose={() =>
                setOverlays((prev) => prev.filter((o) => o.id !== id))
              }
            >
              {fallback}
            </Alert>
          )}
          render={() => content}
        />
      ))}
    </QueryClientProvider>
  );
};
